import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import {
  Activity,
  CalendarDays,
  Droplets,
  HeartPulse,
  LifeBuoy,
  Settings2,
  TriangleAlert,
} from 'lucide-react';

import { AdaptiveBanner } from '@/components/tracker/AdaptiveBanner';
import { CycleCalendar } from '@/components/tracker/CycleCalendar';
import { CycleSettingsDialog } from '@/components/tracker/CycleSettingsDialog';
import { PlanAheadCard } from '@/components/tracker/PlanAheadCard';
import { QuickLogForm } from '@/components/tracker/QuickLogForm';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useCycleEvents } from '@/hooks/useCycleEvents';
import { useCycleLogs } from '@/hooks/useCycleLogs';
import { useCycleSettings } from '@/hooks/useCycleSettings';
import { useToast } from '@/hooks/useToast';
import {
  FLOW_LABELS,
  addDaysISO,
  cycleDayAndPhase,
  daysBetween,
  formatLong,
  formatShort,
  parseISODate,
  painLabel,
  predictedFlareDates,
  toISODate,
  type DayLog,
} from '@/lib/cycle';

function SelectedDaySummary({
  date,
  log,
  event,
}: {
  date: string;
  log: DayLog | undefined;
  event?: string;
}) {
  return (
    <Card className="rounded-3xl shadow-sm">
      <CardContent className="space-y-3 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-bold">{formatShort(date)}</h3>
          {event && (
            <span className="flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800">
              <CalendarDays className="size-3.5" aria-hidden />
              {event}
            </span>
          )}
        </div>
        {log ? (
          <>
            <div className="flex flex-wrap gap-2 text-sm font-semibold">
              <span className="rounded-full bg-lavender px-3 py-1.5 text-lavender-foreground">
                Pain {log.pain}/10 · {painLabel(log.pain)}
              </span>
              <span className="rounded-full bg-blush px-3 py-1.5 text-blush-foreground">
                Flow: {FLOW_LABELS[log.flow]}
              </span>
              {typeof log.heartRate === 'number' && (
                <span className="flex items-center gap-1.5 rounded-full bg-aqua px-3 py-1.5 text-aqua-foreground">
                  <HeartPulse className="size-4" aria-hidden /> {log.heartRate} bpm
                </span>
              )}
              {typeof log.hrv === 'number' && (
                <span className="flex items-center gap-1.5 rounded-full bg-aqua px-3 py-1.5 text-aqua-foreground">
                  <Activity className="size-4" aria-hidden /> HRV {log.hrv} ms
                </span>
              )}
            </div>
            {log.symptoms.length > 0 && (
              <p className="text-sm text-muted-foreground">
                Symptoms: {log.symptoms.join(', ')}
              </p>
            )}
            {log.pain >= 7 && (
              <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-blush-strong/40 bg-blush/50 p-3.5">
                <p className="flex-1 text-sm font-semibold leading-relaxed text-blush-foreground">
                  High pain day — be gentle with yourself and postpone what you can.
                </p>
                <Link
                  to="/care?sos=1"
                  className="inline-flex items-center gap-1.5 rounded-xl bg-blush-strong px-3.5 py-2 text-xs font-bold text-white transition-colors hover:bg-blush-strong/90"
                >
                  <LifeBuoy className="size-4" aria-hidden />
                  SOS guide
                </Link>
              </div>
            )}
          </>
        ) : (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Droplets className="size-4" aria-hidden /> Nothing logged for this day yet — use the
            form below.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function TrackerPage() {
  useSeoMeta({
    title: 'Better Me — Tracker & Adaptive Calendar',
    description:
      'Log pain, flow, heart rate and HRV. See predicted flare-up days and plan around your cycle.',
  });

  const { byDate, upsertLog, prediction } = useCycleLogs();
  const { settings, addSymptom, removeSymptom } = useCycleSettings();
  const { events, setEvent } = useCycleEvents();
  const { toast } = useToast();

  const today = new Date();
  const todayISO = toISODate(today);
  const [selectedDate, setSelectedDate] = useState(todayISO);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const selectedLog = byDate.get(selectedDate);
  const isFutureDay = daysBetween(today, parseISODate(selectedDate)) > 0;

  const cycleInfo = prediction ? cycleDayAndPhase(prediction, today) : null;
  const gentle = !settings.trackFlareUps;

  // Suggested low-pain stretch for rescheduling (same window the banner uses).
  const lightWindow =
    prediction &&
    `${formatShort(addDaysISO(prediction.nextPeriodStart, 6))} – ${formatShort(
      addDaysISO(prediction.nextPeriodStart, 11),
    )}`;

  // Scheduling warnings: plans colliding with logged high pain or predicted flare days.
  const warnings = Object.entries(events)
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([date, text]) => {
      const log = byDate.get(date);
      if (log && log.pain >= 6) {
        return [{ date, text, kind: 'pain' as const, pain: log.pain }];
      }
      if (
        !gentle &&
        !log &&
        prediction &&
        daysBetween(today, parseISODate(date)) >= 0 &&
        predictedFlareDates(prediction, 2).has(date)
      ) {
        return [{ date, text, kind: 'flare' as const }];
      }
      return [];
    })
    .slice(0, 4);

  const handleSave = (log: DayLog) => {
    upsertLog(log);
    toast({
      title: 'Log saved',
      description: `${formatShort(log.date)} · Pain ${log.pain}/10, flow ${FLOW_LABELS[log.flow]}.`,
    });
  };

  return (
    <div className="space-y-5">
      {/* Greeting */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{formatLong(todayISO)}</h1>
          {cycleInfo && (
            <p className="text-base text-muted-foreground">
              Cycle day {cycleInfo.day} · {cycleInfo.phase} phase
              {prediction && prediction.daysUntilPeriod >= 0 && (
                <> · Period predicted in {prediction.daysUntilPeriod} day{prediction.daysUntilPeriod === 1 ? '' : 's'}</>
              )}
            </p>
          )}
        </div>
        <Button
          variant="outline"
          onClick={() => setSettingsOpen(true)}
          className="h-12 shrink-0 rounded-2xl border-lavender-strong/40 bg-lavender/50 px-4 font-semibold text-lavender-foreground hover:bg-lavender"
        >
          <Settings2 className="size-5" aria-hidden />
          <span className="hidden sm:inline">Cycle settings</span>
        </Button>
      </div>

      <AdaptiveBanner
        prediction={prediction}
        gentle={gentle}
        todayPain={byDate.get(todayISO)?.pain}
      />

      {/* Scheduling warnings: plans colliding with high pain or flare days */}
      {warnings.length > 0 && (
        <div className="space-y-2" role="alert">
          {warnings.map((warning) => (
            <div
              key={warning.date}
              className="flex items-start gap-3 rounded-2xl border border-rose-300 bg-rose-50 p-4 text-rose-950"
            >
              <TriangleAlert className="mt-0.5 size-5 shrink-0 text-rose-600" aria-hidden />
              <p className="text-sm leading-relaxed">
                <strong>Scheduling warning · {formatShort(warning.date)}:</strong>{' '}
                {warning.kind === 'pain' ? (
                  <>
                    Severe pain ({warning.pain}/10) is logged alongside your plan{' '}
                    <em>&ldquo;{warning.text}&rdquo;</em>. Consider taking it easy or rescheduling
                    this task{lightWindow ? ` to ${lightWindow}` : ''}.
                  </>
                ) : (
                  <>
                    Your plan <em>&ldquo;{warning.text}&rdquo;</em> falls on a predicted flare-up
                    day. Consider moving it{lightWindow ? ` to ${lightWindow}` : ''}.
                  </>
                )}
              </p>
            </div>
          ))}
        </div>
      )}

      <CycleCalendar
        byDate={byDate}
        prediction={prediction}
        selectedDate={selectedDate}
        onSelect={setSelectedDate}
        gentle={gentle}
        events={events}
      />

      <SelectedDaySummary
        date={selectedDate}
        log={selectedLog}
        event={events[selectedDate]}
      />

      {isFutureDay ? (
        <PlanAheadCard
          date={selectedDate}
          event={events[selectedDate] ?? ''}
          onSaveEvent={(text) => {
            setEvent(selectedDate, text);
            toast({
              title: text.trim() ? 'Plan saved' : 'Plan removed',
              description: text.trim()
                ? `${formatShort(selectedDate)} · "${text.trim()}"`
                : undefined,
            });
          }}
          isFlareDay={
            !gentle && prediction
              ? predictedFlareDates(prediction, 2).has(selectedDate)
              : false
          }
          lightWindow={lightWindow || undefined}
        />
      ) : (
        <QuickLogForm
          date={selectedDate}
          existing={selectedLog}
          onSave={handleSave}
          customSymptoms={settings.customSymptoms}
          onAddSymptom={addSymptom}
          onRemoveSymptom={removeSymptom}
          gentle={gentle}
          showPain={settings.trackPain}
          showFlow={settings.trackFlow}
          showVitals={settings.trackVitals}
          event={events[selectedDate] ?? ''}
          onSaveEvent={(text) => setEvent(selectedDate, text)}
        />
      )}

      <CycleSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}
